import { useEffect, useRef, useState } from 'react';
import { useHighlightStore } from '../lib/highlightStore';

/**
 * Flashes a pink border around the whole page twice, then fades out. Triggered
 * by `useHighlightStore().flash()` (Domina Agent's "Show me") so the user sees
 * what she's pointing at — even on the page they're already on.
 *
 * Driven by plain opacity pulses (no @keyframes, no mount/unmount) so it is
 * reliable across browsers and always visible.
 */
export function PageHighlight() {
  const tick = useHighlightStore((s) => s.tick);
  const [opacity, setOpacity] = useState(0);
  const mounted = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return; // don't flash on first mount
    }
    timers.current.forEach(clearTimeout);
    timers.current = [];
    // Two pulses: on, off, on, off.
    const steps: Array<[number, number]> = [
      [0, 1],
      [180, 0],
      [320, 1],
      [500, 0],
    ];
    for (const [delay, o] of steps) {
      timers.current.push(setTimeout(() => setOpacity(o), delay));
    }
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [tick]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[9999]"
      style={{
        opacity,
        transition: 'opacity 160ms ease-in-out',
        boxShadow: 'inset 0 0 0 6px #ff27a8, inset 0 0 22px 6px rgba(255,39,168,0.35)',
      }}
    />
  );
}
